import React, { MouseEvent } from "react";

import withTitle from "../withTitle";
import { V2 } from "../../Api";
import { DateTime } from "luxon";
import { Link } from "react-router-dom";
import { RouteComponentPropsHistory } from "../../types";
import { formatName, formatDateLocal, formatDateAge, truncateEllipse } from "../../Utilities";
import Checkbox from "../Checkbox";

interface IEmailOutreachResultsViewState {
    isLoading: boolean | string;
    resultCount: number | undefined;
    results: any[];
    searchOffset: number;

    settings: any;
    excludeIds: { [key: string]: boolean };
}

const PAGE_SIZE = 25;

class EmailOutreachResultsView extends React.Component<RouteComponentPropsHistory<any, any>, IEmailOutreachResultsViewState> {
    constructor(props: Readonly<RouteComponentPropsHistory<any, any>>) {
        super(props);

        const state: IEmailOutreachResultsViewState = {
            isLoading: true,
            resultCount: undefined,
            results: [],
            searchOffset: props.location.state?.searchOffset || 0,

            settings: props.location.state?.settings || {},
            excludeIds: props.location.state?.excludeIds || {},
        };

        this.state = state;

        this.search = this.search.bind(this);
        this.previousPage = this.previousPage.bind(this);
        this.nextPage = this.nextPage.bind(this);
        this.goSurvey = this.goSurvey.bind(this);
        this.goBack = this.goBack.bind(this);
        this.saveSearchState = this.saveSearchState.bind(this);
        this.selectSurvey = this.selectSurvey.bind(this);
    }

    public componentDidMount() {
        this.search();
    }

    public render() {
        if ((!this.state.settings.dateStart || !this.state.settings.dateEnd) && !this.state.settings.fairId) {
            return <div className="viewer-root">
                <div className="warning-box">Your search results have expired. Please <Link to="/email-outreach" replace={true}>Go back</Link> and search again.</div>
            </div>;
        }

        return (
            <div className="viewer-root">
                <div>Below are the {this.state.resultCount} questionnaires that match your search. You may look at a questionnaire by clicking on it.</div>
                <div>Once the results are to your liking, you may continue to message template selection.</div>
                &nbsp;
                <div className="action-panel-dialog">
                    <button className="button btn-inline-center" onClick={this.goBack}>Back to Filters</button>
                    <span className="spring" />
                    <Link to={{ pathname: "/email-outreach/template-selection", state: { settings: this.state.settings, excludeIds: this.state.excludeIds, resultCount: this.state.resultCount } }} className="button btn-inline text-with-icon"><span>Template Selection</span><i className="icon-chevron-right" /></Link>
                </div>
                &nbsp;
                {this.state.isLoading === true
                    ? <div className="spinner spinner-center lds-dual-ring" />
                    : typeof this.state.isLoading === "string"
                        ? <span className="error-text">{this.state.isLoading}</span>
                        : <table className="table-vertical">
                            <thead>
                                <tr>
                                    <th className="col-smallest" />
                                    <th>Name</th>
                                    <th className="col-smallest">Birthdate</th>
                                    <th className="col-smallest">Age</th>
                                    <th className="col-smallest">Insurance</th>
                                    <th className="col-smallest">Date Submitted</th>
                                    <th className="col-smallest">Last Email Sent</th>
                                    <th className="col-smallest"># Sent</th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.state.results.map(a => <tr key={a.id} className="clickable" onClick={() => this.goSurvey(a.id)}>
                                    <td className="col-center" onClick={this.stopProp}><Checkbox id={a.id} name={a.id} fancy={true} stopClick={true} checked={!this.state.excludeIds[a.id]} onChange={e => this.selectSurvey(a, !!e.currentTarget.checked)} /></td>
                                    <td>{formatName(a.name)}</td>
                                    <td className="col-right-vt inline" data-caption="Birthdate: ">{formatDateLocal(a.birthdate)}</td>
                                    <td className="col-center inline" data-caption="Age: ">{formatDateAge(a.birthdate)}</td>
                                    <td className="inline" data-caption="Insurance: ">{truncateEllipse(a.insuranceProvider, 15)}</td>
                                    <td className="col-right-vt inline" data-caption="Submitted: ">{formatDateLocal(a.timestamp)}</td>
                                    <td className="col-right-vt inline" data-caption="Last Email: ">{formatDateLocal(a.lastEmailDate) || "-"}</td>
                                    <td className="col-right-vt inline" data-caption="Number of emails sent: ">{a.emailCount || "0"}</td>
                                </tr>)}
                            </tbody>
                        </table>
                }
                &nbsp;
                <div className="action-panel action-panel-noflip">
                    <button className="button push-right text-with-icon" onClick={this.previousPage} disabled={this.state.searchOffset === 0}><i className="icon-angle-left" /><span>Previous</span></button>
                    <button className="button text-with-icon" onClick={this.nextPage} disabled={this.state.results.length < PAGE_SIZE}><span>Next</span><i className="icon-angle-right" /></button>
                </div>
            </div>
        );
    }

    private stopProp(e: MouseEvent<HTMLTableCellElement>) {
        e.stopPropagation();
    }

    private goSurvey(id: string) {
        this.props.history.push(`/surveys/${id}`);
    }

    private goBack() {
        this.props.history.goBack();
    }

    private async search() {
        const { settings } = this.state;

        if ((!settings.dateStart || !settings.dateEnd) && !settings.fairId) {
            return;
        }

        const query = {
            dateStart: settings.dateStart ? DateTime.fromISO(settings.dateStart).startOf("day").toISO() : undefined,
            dateEnd: settings.dateEnd ? DateTime.fromISO(settings.dateEnd).endOf("day").toISO() : undefined,

            ageMin: !parseInt(settings.ageMin) ? undefined : parseInt(settings.ageMin),
            ageMax: !parseInt(settings.ageMax) ? undefined : parseInt(settings.ageMax),

            sex: (settings.sex || null)?.toUpperCase(),

            questionsYes: settings.selectedYesQuestions.map((a: any) => a.id),
            questionsNo: settings.selectedNoQuestions.map((a: any) => a.id),

            fairId: settings.fairId,

            resultFormat: "headers",
            offset: this.state.searchOffset,
            limit: PAGE_SIZE
        };

        this.saveSearchState();

        try {
            this.setState({
                isLoading: true
            });

            const result = await V2.post<any>("survey/query", query);

            this.setState({
                isLoading: false,
                resultCount: result.count,
                results: result.results,
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not execute request"
            });
        }
    }

    private nextPage() {
        this.setState(s => {
            return {
                searchOffset: s.searchOffset + PAGE_SIZE
            };
        }, () => this.search());
    }

    private previousPage() {
        this.setState(s => {
            return {
                searchOffset: Math.max(s.searchOffset - PAGE_SIZE, 0)
            };
        }, () => this.search());
    }

    private saveSearchState() {
        const location = this.props.location;
        if (!location.state) {
            location.state = {};
        }

        location.state.searchOffset = this.state.searchOffset;
        location.state.excludeIds = this.state.excludeIds;

        this.props.history.replace(location);
    }

    private selectSurvey(survey: any, isSelected: boolean) {
        const excludeIds = this.state.excludeIds;

        if (isSelected) {
            delete excludeIds[survey.id];
        }
        else {
            excludeIds[survey.id] = !isSelected;
        }

        this.setState({
            excludeIds
        }, this.saveSearchState);
    }
}

export default withTitle("Email Outreach Results", EmailOutreachResultsView);